/**
 * THE STAGE WORDS — one helper for every surface (Tasks Both Ways slice 3,
 * 10/28, https://claude.ai/artifact/H7gsi8ERjULUpGCpUMZzH8 "The six stages").
 *
 *   New → Seen → Doing → Done      or      Waiting on…  ·  Can't do
 *
 * One color per stage, the same everywhere (the Mine card edge, the Sent by
 * me pill and rail, the Team dots, the iPad): red for New (nobody has opened
 * it — matches the Team dot that was already live), amber for Seen, blue for
 * Doing, green for Done, purple for Waiting, and a solid red with an ✕ for
 * Can't do. Color is never the only signal: every stage has its word, and
 * Can't do and Done carry an icon.
 */
import type { HoldingStage, TaskStage } from "@/lib/tasks";

export type StageKey = "new" | "seen" | "doing" | "done" | "waiting" | "cant_do";

export const STAGE_WORDS: Record<StageKey, string> = {
    new: "New",
    seen: "Seen",
    doing: "Doing",
    done: "Done",
    waiting: "Waiting on…",
    cant_do: "Can't do",
};

/** Tailwind classes per stage: a dot, and a pill (bg + text). */
export const STAGE_LOOK: Record<StageKey, { dot: string; pill: string; edge: string }> = {
    new: {
        dot: "bg-red-600",
        pill: "bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-200",
        edge: "border-l-red-500",
    },
    seen: {
        dot: "bg-amber-500",
        pill: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200",
        edge: "border-l-amber-500",
    },
    doing: {
        dot: "bg-blue-600",
        pill: "bg-blue-100 text-blue-900 dark:bg-blue-950 dark:text-blue-200",
        edge: "border-l-blue-600",
    },
    done: {
        dot: "bg-emerald-600",
        pill: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200",
        edge: "border-l-emerald-600",
    },
    waiting: {
        dot: "bg-violet-600",
        pill: "bg-violet-100 text-violet-900 dark:bg-violet-950 dark:text-violet-200",
        edge: "border-l-violet-600",
    },
    cant_do: { dot: "bg-red-700", pill: "bg-red-700 text-white dark:bg-red-600", edge: "border-l-red-700" },
};

/**
 * Which of the six a task is in. Waiting beats Doing (the ball is outside);
 * Can't do beats everything (it's back with you, in red).
 */
export function stageKeyOf(t: {
    status?: string | null;
    outcome?: string | null;
    stage?: TaskStage | null;
    waitingOn?: string | null;
}): StageKey {
    if (t.stage === "cant_do" || (t.status === "handed_back" && t.outcome === "cant_do")) return "cant_do";
    if (t.status === "handed_back" || t.status === "done" || t.stage === "handed_back" || t.stage === "confirmed")
        return "done";
    if (t.waitingOn) return "waiting";
    if (t.stage === "working") return "doing";
    if (t.stage === "seen") return "seen";
    return "new";
}

/** The Team tab's held-task stages in the same words. */
export function holdingStageKey(stage: HoldingStage): StageKey | "snoozed" {
    switch (stage) {
        case "not_opened":
            return "new";
        case "opened":
            return "seen";
        case "working":
            return "doing";
        case "waiting":
            return "waiting";
        default:
            return "snoozed";
    }
}
